import {NullableStation, Station} from "../../stations/station.service.interface";
import {NullableBoolean, NullableDate, NullableNumber, NullableString} from "../../../types/nullable-types";
import {FormModel} from "../../../models/forms/form.model";
import {FormFields, IFormField} from "../../../models/forms/form-field.interface";
import {IPassengerTypeSelectorsList} from "../../passenger-types/passengers-types.service.interface";
import {computed, IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {FlightScheduleModel} from "../flights-schedule/flight-schedule.model";
import {VoidFlightsScheduleModel} from "../flights-schedule/void-flights-schedule.model";
import {IServiceFactory} from "../../service-factory.interface";
import {IFlightSearchControllerViewModel} from "./flight-search-controller-view-model.interface";
import {IFlightsScheduleViewModel} from "../flights-schedule/flights-schedule-view-model.interface";
import {IFlightScheduleFilter} from "../flights-schedule/flight-schedule-filter.interface";
import {
    IFlightSearchParams
} from "./flight-search-params.interface";
import {WellKnownQueryParams} from "../../window/window.service.interface";
import {Check} from "../../../types/type-checking";
import {PsoUserOptionsEnum} from "./pso.enums";
import {FlightsDatesSelectorModel} from "../dates-selection/flights-dates-selector.model";
import {
    UserSessionClassesOfService
} from "../../dot-rez-api/session/user-session/user-session-classes-of-service";
import {RawSessionStorageKeys} from "../../storage/raw-session-storage/raw-session-storage-keys";
import {IDotRezUserSession} from "../../dot-rez-api/session/user-session/dot-rez-user.session.interface";
import {DotRezUserSession} from "../../dot-rez-api/session/user-session/dot-rez-user.session";
import {IPsoMarket} from "../../stations/pso-market.interface";

export class FlightSearchControllerModel extends FormModel<IFlightSearchParams> implements IFlightSearchControllerViewModel {
    constructor(services: IServiceFactory,
                private readonly _storageKey: RawSessionStorageKeys) {
        super(services);

        this.datesSelector = new FlightsDatesSelectorModel(this, this.services, () => this.services.flightSearch.getLowFareReader());

        makeObservable<this, '_departureFlightSchedule' | '_returnFlightSchedule' | '_psoMarket'>(this, {
            _departureFlightSchedule: observable.ref,
            _returnFlightSchedule: observable.ref,
            _psoMarket: observable.ref,
            departureFlightSchedule: computed,
            returnFlightSchedule: computed,
        });

        this._updatePsoMarket();

        this._reactions.push(reaction(() => this.services.user.isAuthorized,
            async () => {
                this._resetFlightSearchSessionPromise();
                await this._updatePsoMarket();
            }));
    }

    readonly datesSelector: FlightsDatesSelectorModel;

    private _countPassengersFromUrl(): Record<string, number> {
        const result: Record<string, number> = {};
        this.services.passengerTypes.allPassengerTypeCodes.forEach(passengerType => {
            let passengerTypeCount = this.services.window.getUrlParam(passengerType);
            if(passengerTypeCount) {
                result[passengerType] = parseInt(passengerTypeCount);
            }
        });

        return result;
    }

    private _getPsoOptionQueryParam(): PsoUserOptionsEnum {
        const value = this.services.window.getUrlParam(WellKnownQueryParams.PsoUserOption);
        if(Check.isNullOrUndefined(value)) {
            return PsoUserOptionsEnum.None;
        }

        const parsedValue = parseInt(value);
        if(Check.isNumber(parsedValue)) {
            return parsedValue;
        }

        return PsoUserOptionsEnum.None;
    }


    private _getPersistedSearchParams(): (IPersistedSearchParams & {makeOriginReadonly: boolean; makeDestinationReadOnly: boolean}) | null {

        const paramsFromBootstrapScript = this._getSearchParamsFromBootstrapScriptParams() ?? {};
        //paramsFromBootstrapScript.origin = "OLB";
        //paramsFromBootstrapScript.destination = "LIN";
        let paramsOverrides = paramsFromBootstrapScript
                    ?? this.services.rawSessionStorage.getJson<IPersistedSearchParams>(this._storageKey)
                    ?? this._getSearchParamsFromWindowLocationSearch();


        return {
            origin: null,
            destination: null,
            departureDate: null,
            returnDate: null,
            passengers: {},
            isOneWay:  null,
            departureMinPrice: null,
            returnMinPrice: null,
            allowBogo: false,
            psoUserOption: null,
            makeOriginReadonly: Boolean(paramsFromBootstrapScript?.origin),
            makeDestinationReadOnly: Boolean(paramsFromBootstrapScript?.destination),
            ...paramsOverrides

        }
    }

    private _getSearchParamsFromWindowLocationSearch(): Partial<IPersistedSearchParams> {
        const window = this.services.window;
        const isReturnFlight = window.getUrlParam(WellKnownQueryParams.IsRoundTripFlight)?.toLowerCase();
        return {
            origin: window.getUrlParam(WellKnownQueryParams.OriginStation),
            destination: window.getUrlParam(WellKnownQueryParams.DestinationStation),
            departureDate: window.getUrlParam(WellKnownQueryParams.DepartureDate),
            returnDate: window.getUrlParam(WellKnownQueryParams.ReturnDate),
            passengers: this._countPassengersFromUrl(),
            isOneWay:  isReturnFlight ? isReturnFlight !== "true" : null,
            psoUserOption: this._getPsoOptionQueryParam(),

        }
    }

    private _getSearchParamsFromBootstrapScriptParams(): Partial<IPersistedSearchParams> | null {
        const result: Partial<IPersistedSearchParams> = {};
        const scripts = document.getElementsByTagName('script') ?? [];

        for(let i = 0; i < scripts.length; i++) {
            const script = scripts[i];
            if (script.src && script.src.includes('search-mask-bootstrap.js')) {
                // Use URLSearchParams to extract parameters
                const url = new URL(script.src);
                const searchParams = new URLSearchParams(url.search);

                const countPassengersFromUrl = () => {
                    const passengersCount: Record<string, number> = {};
                    this.services.passengerTypes.allPassengerTypeCodes.forEach(passengerType => {
                        let passengerTypeCount = searchParams.get(passengerType);
                        if(passengerTypeCount) {
                            passengersCount[passengerType] = parseInt(passengerTypeCount);
                        }
                    });

                    return passengersCount;
                }



                result.origin = searchParams.get(WellKnownQueryParams.OriginStation) || null;
                result.destination = searchParams.get(WellKnownQueryParams.DestinationStation) || null;
                result.departureDate= searchParams.get(WellKnownQueryParams.DepartureDate) || null;
                result.returnDate = searchParams.get(WellKnownQueryParams.ReturnDate) || null;
                const isReturnFlight = searchParams.get(WellKnownQueryParams.IsRoundTripFlight)?.toLowerCase();
                result.isOneWay = isReturnFlight ? isReturnFlight !== "true" : null;
                result.passengers = countPassengersFromUrl();

                break;
            }
        }

        if(Check.isEmpty(result)) {
            return null;
        }

        return result;
    }

    protected _createFields(): FormFields<IFlightSearchParams> {
        const persistedFieldsValues = this._getPersistedSearchParams();
        const lang = this.services.language;
        return {
            isOneWay: this._createField<boolean>({
                fieldName: () => 'Is one way',
                defaultValue: persistedFieldsValues?.isOneWay ?? false,
            }),
            origin: this._createField<Station>({
                fieldName: () => lang.translate('Departure'),
                isRequired: true,
                defaultValue: this.services.stations.tryGetStation(persistedFieldsValues?.origin),
                isReadOnly: persistedFieldsValues?.makeOriginReadonly ?? false,
            }),
            destination: this._createField<Station>({
                fieldName: () => lang.translate('Destination'),
                isRequired: true,
                defaultValue: this.services.stations.tryGetStation(persistedFieldsValues?.destination),
                isReadOnly: persistedFieldsValues?.makeDestinationReadOnly ?? false,
            }),
            psoUserOption: this._createField<PsoUserOptionsEnum>({
               fieldName: () => "",
               isRequired: false,
               defaultValue: persistedFieldsValues?.psoUserOption ?? PsoUserOptionsEnum.None,
               isHidden: () => !this.supportsPsoMarket()
            }),
            departureDate: this._createField<Date>({
                fieldName: () => lang.translate('Departure date'),
                isRequired: true,
                defaultValue: this.services.time.tryConvertToDate(persistedFieldsValues?.departureDate)
            }),
            returnDate: this._createField<Date>({
                fieldName: () => lang.translate('Return date'),
                isRequired: false, //() => !this.isOneWay,
                defaultValue: this.services.time.tryConvertToDate(persistedFieldsValues?.returnDate)
            }),
            departureMinPrice: this._createField<NullableNumber>({
                fieldName: () => 'Minimum price',
                isRequired: false,
                defaultValue: persistedFieldsValues?.departureMinPrice || null,
            }),
            returnMinPrice: this._createField<NullableNumber>({
                fieldName: () => 'Minimum price',
                isRequired: false,
                defaultValue: persistedFieldsValues?.returnMinPrice || null,
            }),
            passengers: this._createField<IPassengerTypeSelectorsList>({
                fieldName: () => lang.translate('Passengers'),
                defaultValue: this.services.passengerTypes.getPassengersSelectors(() => false, persistedFieldsValues?.passengers ?? {}),
                validate: () => {
                    if(this.passengers.countAllWithoutInfants() <= 0) {
                        return this.services.language.translate('You must select at least one passenger')
                    }
                    return null;
                }
            }),
            allowBogo: this._createField<boolean>({
                fieldName: () => 'Allow Bogo',
                isRequired: false,
                defaultValue: Boolean(persistedFieldsValues?.allowBogo),
            }),
            departureFlightScheduleFilter: this._createField<IFlightScheduleFilter>({
                fieldName: () => 'Departure flight schedule filter',
                isRequired: false
            }),
            returnFlightScheduleFilter: this._createField<IFlightScheduleFilter>({
                fieldName: () => 'Return flight schedule filter',
                isRequired: false
            })
        };
    }


    supportsPsoMarket(): boolean {
        return Boolean(this.psoMarket);
    }

    private _getPsoAgentName(): NullableString {
        if(this.services.user.isAuthorized) {
            return null;
        }

        if(this.psoUserOption === PsoUserOptionsEnum.None) {
            return null;
        }

        const psoMarket = this.psoMarket;

        if(!psoMarket) {
            return null;
        }

        return psoMarket.getAgentForUserOption(this.psoUserOption);
    }

    public getAnonymousAgentName(): NullableString {
        // if(this.fields.agent.value) {
        //     return this.fields.agent.value;
        // }

        return this._getPsoAgentName();
    }

    private async _createFlightSearchSession(): Promise<IDotRezUserSession> {

        let agentName = this.getAnonymousAgentName();
        this._previousUsedAgent = agentName;

        if(agentName) {
            const dotRezToken = await this.services.dotRezApi.getDeepLinkAgentToken({
                agentName: agentName
            });

            return new DotRezUserSession(dotRezToken, this.services);

        } else {
            return this.services.user.getSession();
        }
    }

    private _previousUsedAgent: NullableString = null;
    private _updateFlightSearchSessionAgent(): Promise<IDotRezUserSession> {

        this._flightSearchSessionPromise = this.getFlightSearchSession().then(async session => {

            const currentAgentName = this.getAnonymousAgentName();

            if(currentAgentName === this._previousUsedAgent) {
                return session;
            }

            this._previousUsedAgent = currentAgentName;

            if(currentAgentName) {
                try {
                    await session.impersonateWithAgent({
                        agent: currentAgentName
                    });
                } catch(err) {
                    this.services.logger.error(`Failed to impersonate with agent ${currentAgentName}`, err);
                }

            } else if(!this.services.user.isAuthorized){
                try {
                    await session.impersonateToAnonymous();
                } catch (err) {
                    this.services.logger.error(`Failed to impersonate to anonymous`, err);
                }

            }

            return session;
        });

        return this._flightSearchSessionPromise;

    }


    private async _switchPsoMarket(): Promise<void> {
        await this._updatePsoMarket();
        await this._updateFlightSearchSessionAgent();
    }

    private _resetFlightSearchSessionPromise(): void {
        this._flightSearchSessionPromise = null;
        this._previousUsedAgent = null;
    }

    private _flightSearchSessionPromise: Promise<IDotRezUserSession> | null = null;
    getFlightSearchSession(): Promise<IDotRezUserSession> {
        if(!this._flightSearchSessionPromise) {
            this._flightSearchSessionPromise = this._createFlightSearchSession();
        }

        return this._flightSearchSessionPromise;
    }


    private async _getUserClassesOfService(): Promise<UserSessionClassesOfService> {
        const session = await this.getFlightSearchSession();
        return await session.getClassesOfService();

    }

    private _psoMarket: IPsoMarket | null = null;
    get psoMarket(): IPsoMarket | null {
        return this._psoMarket;
    }

    private set psoMarket(value: IPsoMarket | null) {
        runInAction(() => {
            this._psoMarket = value;
        });

    }

    private _getRoutePsoMarket(): IPsoMarket | null {
        return this.services.stations.getPsoMarket(this.origin, this.destination, {
            bookingDate: this.services.time.currentDate,
            departureDate: this.departureDate,
        });
    }

    private async _getPsoMarket(): Promise<IPsoMarket | null> {
        const psoMarket = this._getRoutePsoMarket();

        if(!psoMarket) {
            return null;
        }

        if(!this.services.user.isAuthorized) {
            return psoMarket;
        }

        const userClassesOfService = await this._getUserClassesOfService();

        if(psoMarket.canBeAppliedForUserClassesOfService(userClassesOfService)) {
            return psoMarket;
        }

        return null;
    }



    private _updatePsoMarketPromise: Promise<void> = Promise.resolve();
    private _updatePsoMarket(): Promise<void> {
        this._updatePsoMarketPromise = this._getPsoMarket().then(psoMarket => {
            this.psoMarket = psoMarket;
            if(!psoMarket) {
                this._clearPsoMarketOptionsSelection();
            }
        }).catch(err => {
            this.services.logger.error('Failed to update PSO market', err);
            this._updatePsoMarketPromise = Promise.resolve();
            this.psoMarket = null;
        });
        return this._updatePsoMarketPromise;
    }

    async getClassesOfServiceForSearch(): Promise<string[]> {

        if(!this.services.user.isAuthorized) {
            return [];
        }

        const market = await this._getPsoMarket();
        if(!market) {
            return [];
        }

        if(!market.isUsingClassesOfService) {
            return [];
        }

        const userClassesOfService = await this._getUserClassesOfService();

       return market.getClassesOfServiceForSearch(userClassesOfService, this.psoUserOption);
    }

    get availableOriginStations(): Station[] {
        if(this.isDestinationReadonly) {
            if(this.destination) {
                return this.destination.markets;
            } else {
                return [];
            }

        }
        return this.services.stations.getAllStations();
    }

    get availableDestinationStations(): Station[] {

        if(this.isDestinationReadonly) {
            if(this.destination) {
                return [this.destination];
            } else {
                return [];
            }
        }

        if(this.origin) {
            return this.origin.markets;
        }
        return [];
    }

    get recentlySearchedOrigins(): Station[] {
        return this.services.flightSearch.getRecentlySearchedOrigins();
    }

    get recentlySearchedDestinations(): Station[] {
        const stations = this.services.flightSearch.getRecentlySearchedDestinations();
        return stations.filter(s => s.stationCode !== this.origin?.stationCode)
    }


    get isOneWay(): boolean {
        return this.fields.isOneWay.value || false;
    }

    set isOneWay(value: boolean) {
        this.fields.isOneWay.setValue(value);
    }


    get origin(): NullableStation {
        return this.fields.origin.value;
    }

    set origin(value: NullableStation) {
        this.fields.origin.setValue(value);
    }

    get isOriginReadonly(): boolean {
        return this.fields.origin.isReadOnly;
    }


    get destination(): NullableStation {
        return this.fields.destination.value;
    }

    set destination(value: NullableStation) {
        this.fields.destination.setValue(value);
    }

    get isDestinationReadonly(): boolean {
        return this.fields.destination.isReadOnly;
    }


    get psoUserOption(): PsoUserOptionsEnum {
        return this.fields.psoUserOption.value ?? PsoUserOptionsEnum.None;
    }

    set psoUserOption(value: PsoUserOptionsEnum) {
        this.fields.psoUserOption.setValue(value);
    }

    get departureDate(): NullableDate {
        return this.fields.departureDate.value;
    }

    set departureDate(value: NullableDate) {
        this.fields.departureDate.setValue(value);
    }

    get departureDateError(): NullableString {
        return this.fields.departureDate.error;
    }


    get returnDate(): NullableDate {
        if(this.isOneWay) {
            return null;
        }
        return this.fields.returnDate.value;
    }

    set returnDate(value: NullableDate) {
        this.fields.returnDate.setValue(value);
    }

    get returnDateError(): NullableString {
        return this.fields.returnDate.error;
    }


    get passengers(): IPassengerTypeSelectorsList {
        return this.fields.passengers.value!
    }

    set passengers(value: IPassengerTypeSelectorsList) {
        this.fields.passengers.setValue(value);
    }

    get departureMinPrice(): NullableNumber {
        return this.fields.departureMinPrice.value;
    }

    set departureMinPrice(value: NullableNumber) {
        this.fields.departureMinPrice.setValue(value);
    }

    get returnMinPrice(): NullableNumber {
        return this.fields.returnMinPrice.value;
    }

    set returnMinPrice(value: NullableNumber) {
        this.fields.returnMinPrice.setValue(value);
    }

    get allowBogo(): boolean {
        return Boolean(this.fields.allowBogo.value);
    }

    set allowBogo(value: boolean) {
        this.fields.allowBogo.setValue(value);
    }

    get departureFlightScheduleFilter(): IFlightScheduleFilter | null {
        return this.fields.departureFlightScheduleFilter.value;
    }

    set departureFlightScheduleFilter(value: IFlightScheduleFilter | null) {
        this.fields.departureFlightScheduleFilter.setValue(value);
    }

    get returnFlightScheduleFilter(): IFlightScheduleFilter | null {
        return this.fields.returnFlightScheduleFilter.value;
    }

    set returnFlightScheduleFilter(value: IFlightScheduleFilter | null) {
        this.fields.returnFlightScheduleFilter.setValue(value);
    }

    validateFlightDatesSelection(): string {


        if(this.isOneWay) {
            if(!this.departureDate) {
                return this.services.language.translate('Select the departure date');
            }
        } else {
            if(!this.departureDate || !this.returnDate) {
                return this.services.language.translate('Select the flights dates');
            }
        }

        return "";
    }

    private _reactions: IReactionDisposer[] = [];

    private _clearPsoMarketOptionsSelection() {
        this.fields.psoUserOption.setValue(PsoUserOptionsEnum.None);
    }

    private _resetUsePsoMarketSelection(): void {
        this._clearPsoMarketOptionsSelection();
        this._updatePsoMarket();

    }

    protected _onFieldsCreated(fields: FormFields<IFlightSearchParams>) {
        super._onFieldsCreated(fields);

        fields.origin.onChange(() => {
            this._resetDates();
            if(fields.destination.value !== null && !this.isDestinationReadonly) {
                fields.destination.setValue(null);
            } else {
                this._resetFlightsSchedule();
            }

            this._resetUsePsoMarketSelection();
        });

        fields.destination.onChange(() => {
            this._resetDates();
            this._resetFlightsSchedule();
            this._resetUsePsoMarketSelection();
            this.datesSelector.updateAvailableMonths();
        });

        fields.psoUserOption.onChange(() => {
            if(!this.services.user.isAuthorized) {
                // Only when the user is anonymous we reset the session because for authorized user we always use the user session.
                // For anonymous user on PSO we need to switch agents based on user PSO selection.
                this._updateFlightSearchSessionAgent();
            }

        });

        fields.departureDate.onChange(async () => {
            await this._switchPsoMarket();
        });


        fields.departureFlightScheduleFilter.onChange(() => {
            this._resetFlightsSchedule();
        });

        fields.returnFlightScheduleFilter.onChange(() => {
            this._resetFlightsSchedule();
        })


        this._reactions.push(reaction(() =>  fields.passengers.value?.getAll().map(p => p.count),
                                        () => this._persistFieldsValues()));

    }

    private _resetDates(): void {
        this.fields.departureDate.setValue(null);
        this.fields.returnDate.setValue(null);
    }

    protected _onFieldChanged(field: IFormField) {
        super._onFieldChanged(field);
        this._persistFieldsValues();
    }

    private _resetFlightsSchedule(): void {
        runInAction(() => {
            this._departureFlightSchedule = null;
            this._returnFlightSchedule = null;
        })
    }


    private _departureFlightSchedule: IFlightsScheduleViewModel | null = null;
    get departureFlightSchedule(): IFlightsScheduleViewModel {
        return this._getFlightSchedule(
            this.origin,
            this.destination,
            () => null,
            () => this._departureFlightSchedule,
            schedule => this._departureFlightSchedule = schedule,
            this.departureFlightScheduleFilter);
    }

    private _getMinAllowedReturnDate(): NullableDate {

        if(this.isOneWay) {
            return null;
        }

        return this.departureDate;

    }
    private _returnFlightSchedule: IFlightsScheduleViewModel | null = null;
    get returnFlightSchedule(): IFlightsScheduleViewModel {
        return this._getFlightSchedule(
            this.destination,
            this.origin,
            () =>  this._getMinAllowedReturnDate(),
            () => this._returnFlightSchedule,
            schedule => this._returnFlightSchedule = schedule,
            this.returnFlightScheduleFilter);
    }

    private _getFlightSchedule(origin: NullableStation,
                               destination: NullableStation,
                               getMinAllowedDate: () => NullableDate,
                               getCurrentSchedule: () => IFlightsScheduleViewModel | null,
                               setCurrentSchedule: (schedule: IFlightsScheduleViewModel) => void,
                               scheduleFilter: IFlightScheduleFilter | null): IFlightsScheduleViewModel{

        const currentSchedule = getCurrentSchedule();
        if(currentSchedule) {
            return currentSchedule;
        }

        if(!origin || !destination) {
            return new VoidFlightsScheduleModel();
        }

        if(origin.stationCode === destination.stationCode) {
            return new VoidFlightsScheduleModel();
        }

        const newSchedule = new FlightScheduleModel(this.services, origin, destination, getMinAllowedDate, scheduleFilter);

        runInAction(() => {
            setCurrentSchedule(newSchedule);
        });

        return newSchedule;
    }


    private  _persistFieldsValues(): void {

        const persistedFieldValues: IPersistedSearchParams = {
            isOneWay: this.fields.isOneWay.value,
            origin: this.fields.origin.value?.stationCode || null,
            destination: this.fields.destination.value?.stationCode || null,
            departureDate: this.services.time.formatYYYY_MM_DD(this.fields.departureDate.value),
            returnDate: this.services.time.formatYYYY_MM_DD(this.fields.returnDate.value),
            passengers: this._serializePassengers(),
            departureMinPrice: this.fields.departureMinPrice.value,
            returnMinPrice: this.fields.returnMinPrice.value,
            allowBogo: this.allowBogo,
            psoUserOption: this.fields.psoUserOption.value,
        }

        this.services.rawSessionStorage.setJson(this._storageKey, persistedFieldValues);
    }

    private _serializePassengers(): Record<string, number> {
        if(this.fields.passengers.value) {
            return this.fields.passengers.value.getAll().toDictionaryOfType(item => item.getCurrentCode(), item => item.count);
        } else {
            return {}
        }
    }

    dispose(): void {
        this.services.rawSessionStorage.removeItem(this._storageKey);
        this._reactions.forEach(disposer => disposer());
        this._reactions = [];
    }

}


interface IPersistedSearchParams {
    isOneWay: NullableBoolean;
    origin: NullableString;
    destination: NullableString;
    departureDate: NullableString;
    returnDate: NullableString;
    passengers: Record<string, number>;
    departureMinPrice: NullableNumber;
    returnMinPrice: NullableNumber;
    allowBogo: boolean;
    psoUserOption: PsoUserOptionsEnum | null;

}
